import React from 'react';
import { Alert, Box, IconButton, LinearProgress, Typography } from '@mui/material';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

interface PhotoCaptureProps {
  label: string;
  hint?: string;
  /** Public url of the uploaded photo, empty until one is uploaded. */
  url: string;
  /** Upload percent while an upload is running, null otherwise. */
  progress: number | null;
  error?: string;
  onSelect: (file: File) => void;
  onClear: () => void;
}

const BUTTON_HEIGHT = 48;

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * One photo slot. A single native file input lets the phone offer Take Photo, Photo Library and
 * Choose File in its own sheet.
 */
const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  label,
  hint,
  url,
  progress,
  error,
  onSelect,
  onClear,
}) => {
  const uploading = progress !== null;
  const hasPhoto = !!url && !uploading;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the value so choosing the same photo again still fires a change
    event.target.value = '';
    if (file) {
      onSelect(file);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {hasPhoto && (
          <CheckCircleIcon fontSize="small" style={{ color: '#2e7d32' }} aria-label="Photo added" />
        )}
      </Box>
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      )}

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          pt: '75%',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'common.white',
          border: theme =>
            `2px ${hasPhoto ? 'solid' : 'dashed'} ${
              hasPhoto ? theme.palette.success.main : theme.palette.grey[400]
            }`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {hasPhoto && (
            <img
              src={url}
              alt={`${label} preview`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
          {!hasPhoto && !uploading && (
            <Box textAlign="center" color="text.secondary">
              <PhotoCameraIcon style={{ fontSize: 48 }} />
              <Typography variant="body2">No photo yet</Typography>
            </Box>
          )}
          {uploading && (
            <Box sx={{ width: '70%' }} role="status" aria-live="polite">
              <Typography variant="body2" align="center" sx={{ mb: 1 }}>
                {progress === 0 ? 'Preparing photo…' : `Uploading… ${progress}%`}
              </Typography>
              <LinearProgress
                variant={progress === 0 ? 'indeterminate' : 'determinate'}
                value={progress ?? 0}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box display="flex" gap={1} sx={{ mt: 1 }}>
        {/*
          A plain <label> around a visually-hidden file input, styled like a contained or outlined
          button. The browser opens the camera / photo library sheet natively, with no script in the
          tap path. An MUI Button used as this label, and a hidden input opened from a script, both
          threw on a real phone before any picker opened; this form is the one confirmed working there.
        */}
        <Box
          component="label"
          aria-disabled={uploading}
          sx={{
            // Containing block for the absolutely positioned hidden input. Without it the input is
            // positioned against the page, escapes the app wrapper's overflow clip and lets the
            // window scroll past the form (Android Chrome), and the browser scrolls it into view.
            position: 'relative',
            flex: 1,
            minHeight: BUTTON_HEIGHT,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 2,
            borderRadius: 1,
            typography: 'button',
            cursor: uploading ? 'default' : 'pointer',
            userSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
            opacity: uploading ? 0.5 : 1,
            '&:active': { opacity: uploading ? 0.5 : 0.8 },
            '&:focus-within': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
            ...(hasPhoto
              ? {
                  color: 'primary.main',
                  border: 1,
                  borderColor: 'primary.main',
                  bgcolor: 'transparent',
                }
              : { color: 'primary.contrastText', bgcolor: 'primary.main' }),
          }}
        >
          <PhotoCameraIcon fontSize="small" />
          {hasPhoto ? 'Replace photo' : 'Add photo'}
          <input
            type="file"
            accept="image/*"
            style={VISUALLY_HIDDEN}
            disabled={uploading}
            data-testid={`${label}-input`}
            onChange={handleChange}
          />
        </Box>
        {hasPhoto && (
          <IconButton
            aria-label={`Remove ${label}`}
            onClick={onClear}
            sx={{
              width: BUTTON_HEIGHT,
              height: BUTTON_HEIGHT,
              border: 1,
              borderColor: 'grey.400',
              borderRadius: 1,
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default PhotoCapture;
